'use client'

import React from 'react'
import Image from 'next/image'

export interface BrandLogoProps {
  type?: 'full' | 'icon' | 'badge'
  theme?: 'dark' | 'light' | 'auto'
  className?: string
  width?: number
  height?: number
  priority?: boolean
  alt?: string
}

/**
 * Official Parts4Drive Brand Logo Component
 * - theme="dark": For dark navy/black containers (#060D1E, #050B18, dark mode)
 * - theme="light": For white/light containers (cards, modals, invoices)
 * - theme="auto": Responds to Tailwind dark: class
 */
export default function BrandLogo({
  type = 'full',
  theme = 'dark',
  className = '',
  width = 170,
  height = 42,
  priority = false,
  alt = 'Parts4Drive'
}: BrandLogoProps) {
  if (type === 'icon') {
    if (theme === 'dark') {
      return (
        <div className={`relative flex items-center justify-center shrink-0 ${className}`}>
          <Image
            src="/darkmodeicon.webp"
            alt={alt}
            width={width || 40}
            height={height || 40}
            className="w-full h-full object-contain"
            priority={priority}
          />
        </div>
      )
    }

    if (theme === 'light') {
      return (
        <div className={`relative flex items-center justify-center shrink-0 ${className}`}>
          <Image
            src="/lightmodeicon.webp"
            alt={alt}
            width={width || 40}
            height={height || 40}
            className="w-full h-full object-contain"
            priority={priority}
          />
        </div>
      )
    }

    return (
      <div className={`relative flex items-center justify-center shrink-0 ${className}`}>
        <Image
          src="/lightmodeicon.webp"
          alt={alt}
          width={width || 40}
          height={height || 40}
          className="w-full h-full object-contain dark:hidden"
          priority={priority}
        />
        <Image
          src="/darkmodeicon.webp"
          alt={alt}
          width={width || 40}
          height={height || 40}
          className="w-full h-full object-contain hidden dark:block"
          priority={priority}
        />
      </div>
    )
  }

  // Full Logo (Mark + Typography)
  if (theme === 'dark') {
    return (
      <div className={`relative flex items-center shrink-0 ${className}`}>
        <picture>
          <source srcSet="/darkmodelogo.webp" type="image/webp" />
          <img
            src="/darkmodelogo.png"
            alt={alt}
            width={width}
            height={height}
            className="h-9 sm:h-10 w-auto object-contain"
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
          />
        </picture>
      </div>
    )
  }

  if (theme === 'light') {
    return (
      <div className={`relative flex items-center shrink-0 ${className}`}>
        <picture>
          <source srcSet="/lightmodelogo.webp" type="image/webp" />
          <img
            src="/lightmodelogo.png"
            alt={alt}
            width={width}
            height={height}
            className="h-9 sm:h-10 w-auto object-contain"
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
          />
        </picture>
      </div>
    )
  }

  // Auto theme
  return (
    <div className={`relative flex items-center shrink-0 ${className}`}>
      <picture className="dark:hidden">
        <source srcSet="/lightmodelogo.webp" type="image/webp" />
        <img
          src="/lightmodelogo.png"
          alt={alt}
          width={width}
          height={height}
          className="h-9 sm:h-10 w-auto object-contain"
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      </picture>
      <picture className="hidden dark:block">
        <source srcSet="/darkmodelogo.webp" type="image/webp" />
        <img
          src="/darkmodelogo.png"
          alt={alt}
          width={width}
          height={height}
          className="h-9 sm:h-10 w-auto object-contain"
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      </picture>
    </div>
  )
}

export { BrandLogo }
